import React, { useState, useMemo, useEffect, useCallback } from "react";
import { ChevronRight } from "lucide-react";
import axios from "axios";
import { storeInfo } from "../../utils/slugify";

import { buyNowProduct } from "../../utils/buyApi";

const DEAL_LABELS = ["Poor", "Fair", "Good", "Excellent"];
const DEAL_COLORS = ["#EF4444", "#F59E0B", "#FCD34D", "#10B981"];
const ANGLES = [290, 340, 20, 70];

const DealScore = ({ storeComparison, productData }) => {
  const [needleAngle, setNeedleAngle] = useState(0);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const allPrices = useMemo(() => {
    const out = [];
    const history = productData?.graph_data || [];
    
    history.forEach(entry => {
      if (typeof entry.min_price === "number") out.push(entry.min_price);
      if (typeof entry.max_price === "number") out.push(entry.max_price);
    });
    
    // Add current price as a data point
    if (productData.price) out.push(productData.price);
    
    return out;
  }, [productData]);

  const lowestPrice = useMemo(() => {
    if (!allPrices || allPrices.length === 0) return productData.price;
    return Math.min(...allPrices);
  }, [allPrices, productData.price]);

  const highestPrice = useMemo(() => {
    if (!allPrices || allPrices.length === 0) return productData.price;
    return Math.max(...allPrices);
  }, [allPrices, productData.price]);

  const last60DaysPrices = useMemo(() => {
    const cutoff = Date.now() - 60 * 24 * 60 * 60 * 1000;
    const out = [];
    const history = productData?.graph_data || [];

    history.forEach(entry => {
      const parsedTs = Date.parse(entry.time);
      if (isNaN(parsedTs) || parsedTs < cutoff) return;

      if (typeof entry.min_price === "number") out.push(entry.min_price);
    });

    if (out.length === 0 && productData.price) out.push(productData.price);
    out.sort((a, b) => a - b);
    return out;
  }, [productData]);

  const dealScore = useMemo(() => {
    if (!last60DaysPrices || last60DaysPrices.length === 0) return 3; // Default to Good
    const arr = last60DaysPrices;
    const len = arr.length;

    const at = (pct) => {
      const idx = Math.floor((len * pct) / 100);
      const clamped = Math.max(0, Math.min(len - 1, idx));
      return arr[clamped];
    };

    const p15 = at(15);
    const p50 = at(50);
    const p75 = at(75);
    const latest = productData.price;

    if (latest < p15) return 4;
    if (latest <= p50) return 3;
    if (latest <= p75) return 2;
    return 1;
  }, [last60DaysPrices, productData.price]);

  useEffect(() => {
    const angle = ANGLES[Math.max(0, Math.min(3, dealScore - 1))] ?? ANGLES[0];
    setNeedleAngle(angle);
  }, [dealScore]);

  const totalScore = useMemo(() => {
    if (!last60DaysPrices || last60DaysPrices.length === 0) return 70;
    const arr = last60DaysPrices;
    const lowest = arr[0];
    const highest = arr[arr.length - 1];
    const current = productData.price;

    if (highest === lowest) return 70;
    
    // Calculate percentage position (0 is highest, 100 is lowest)
    const score = Math.round(((highest - current) / (highest - lowest)) * 100);
    return Math.max(0, Math.min(100, score));
  }, [last60DaysPrices, productData.price]);

  const verdict = useMemo(() => {
    const score = totalScore;
    const productTitle = productData.title || "this product";

    if (score >= 90) {
      return {
        emoji: "🔥",
        heading: "Elite Deal",
        subheading: "Best price guaranteed!",
        summary: `This is the perfect time to buy ${productTitle}. Our analysis shows that the current price is at an all-time low or a major price floor. With no upcoming sales expected to beat this price, you are getting the maximum possible value. Grab it now before the price increases or stock runs out.`,
        type: "elite-deal",
        color: "#10B981",
        bg: "#ECFDF5"
      };
    } else if (score >= 75) {
      return {
        emoji: "✅",
        heading: "Great Value",
        subheading: "Highly recommended.",
        summary: `${productTitle} is currently offered at a very competitive price, significantly lower than its 45-day average. While a sale might be a few weeks away, the current discount is strong enough that waiting is unlikely to save you much more. This is a smart buy for anyone looking for a great deal today.`,
        type: "great-value",
        color: "#059669",
        bg: "#F0FDF4"
      };
    } else if (score >= 60) {
      return {
        emoji: "⚖️",
        heading: "Fair Price",
        subheading: "Decent price now",
        summary: `The current price for ${productTitle} is stable and fair. It's hovering around the recent market average, meaning you aren't overpaying, but you aren't getting a massive discount either. If you need it urgently, go ahead; otherwise, you might save more by waiting.`,
        type: "fair-price",
        color: "#F59E0B",
        bg: "#FFFBEB"
      };
    } else if (score >= 40) {
      return {
        emoji: "⏳",
        heading: "Wait & Watch",
        subheading: "Hold on! Better price likely.",
        summary: `We suggest waiting before you buy ${productTitle}. Our data indicates either a sale is approaching within 15–30 days or the current price is slightly inflated compared to recent weeks. Add a price alert, and we'll notify you when it hits the "Great Value" zone.`,
        type: "wait-watch",
        color: "#D97706",
        bg: "#FFF7ED"
      };
    } else {
      return {
        emoji: "❌",
        heading: "Bad Timing",
        subheading: "Overpriced. Avoid right now.",
        summary: `Now is not the time to buy ${productTitle}. The price is currently at a peak, significantly higher than its historical minimums and recent averages. With a major sale event on the horizon, the price is expected to drop soon. Save your money and wait for a price correction.`,
        type: "bad-timing",
        color: "#DC2626",
        bg: "#FEF2F2"
      };
    }
  }, [totalScore, productData.title]);

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 font-inter">
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr,auto,1.5fr] gap-0">
        {/* Deal Score Gauge - Compact Vertical alignment */}
        <div className="flex flex-col py-6 lg:pr-12 h-full justify-center">
          <div className="space-y-4">
            <h3 className="text-xs font-black text-slate-400 text-center uppercase tracking-[0.3em]">Deal Analysis</h3>

            <div className="relative flex justify-center items-center py-1 transition-transform">
              {isClient ? (
                <svg width="300" height="175" viewBox="0 0 300 170" className="overflow-visible">
                  {/* Gauge logic ... */}
                  {DEAL_LABELS.map((label, index) => {
                    const gapSize = 4;
                    const totalAngle = 180;
                    const segmentAngle = (totalAngle / 4) - gapSize;
                    const startAngle = 180 + (index * (totalAngle / 4)) + (gapSize / 2);
                    const endAngle = startAngle + segmentAngle;
                    const radius = 120;
                    const innerRadius = 90;
                    const cx = 150;
                    const cy = 150;

                    const x1Outer = cx + radius * Math.cos((startAngle * Math.PI) / 180);
                    const y1Outer = cy + radius * Math.sin((startAngle * Math.PI) / 180);
                    const x2Outer = cx + radius * Math.cos((endAngle * Math.PI) / 180);
                    const y2Outer = cy + radius * Math.sin((endAngle * Math.PI) / 180);

                    const x1Inner = cx + innerRadius * Math.cos((startAngle * Math.PI) / 180);
                    const y1Inner = cy + innerRadius * Math.sin((startAngle * Math.PI) / 180);
                    const x2Inner = cx + innerRadius * Math.cos((endAngle * Math.PI) / 180);
                    const y2Inner = cy + innerRadius * Math.sin((endAngle * Math.PI) / 180);

                    return (
                      <path
                        key={index}
                        d={`M ${x1Inner} ${y1Inner} L ${x1Outer} ${y1Outer} A ${radius} ${radius} 0 0 1 ${x2Outer} ${y2Outer} L ${x2Inner} ${y2Inner} A ${innerRadius} ${innerRadius} 0 0 0 ${x1Inner} ${y1Inner} Z`}
                        fill={DEAL_COLORS[index]}
                        opacity="0.9"
                        className="transition-opacity duration-300"
                      />
                    );
                  })}
                  <path d="M 30 150 A 120 120 0 0 1 270 150 L 270 150 A 85 85 0 0 0 30 150 Z" fill="white" />
                  {DEAL_LABELS.map((label, index) => {
                    const totalAngle = 180;
                    const gapSize = 4;
                    const segmentAngle = (totalAngle / 4) - gapSize;
                    const startAngle = 180 + (index * (totalAngle / 4)) + (gapSize / 2);
                    const radius = 105;
                    const pathId = `textPath-${index}`;
                    const x1 = 150 + radius * Math.cos((startAngle * Math.PI) / 180);
                    const y1 = 150 + radius * Math.sin((startAngle * Math.PI) / 180);
                    const x2 = 150 + radius * Math.cos(((startAngle + segmentAngle) * Math.PI) / 180);
                    const y2 = 150 + radius * Math.sin(((startAngle + segmentAngle) * Math.PI) / 180);
                    return (
                      <g key={index}>
                        <defs><path id={pathId} d={`M ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2}`} fill="none" /></defs>
                        <text className="text-[11px] font-bold uppercase tracking-widest" fill="#fff">
                          <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">{label}</textPath>
                        </text>
                      </g>
                    );
                  })}
                  <g style={{ transform: `rotate(${needleAngle}deg)`, transformOrigin: "150px 150px", transition: "transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)" }}>
                    <path d="M 150 150 L 147 155 L 150 65 L 153 155 Z" fill="#00000020" transform="translate(2, 2)" />
                    <path d="M 150 150 L 147 155 L 150 65 L 153 155 Z" fill="#7E54F2" />
                  </g>
                  <circle cx="150" cy="150" r="10" fill="#7E54F2" /><circle cx="150" cy="150" r="6" fill="#2c3562" />
                </svg>
              ) : (
                <div className="w-[300px] h-[175px] flex items-center justify-center">
                  <div className="text-gray-400 font-bold uppercase tracking-widest text-xs">Loading...</div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl p-3 text-center border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Current</p>
              <p className="text-base font-bold text-[#7E54F2]">₹{productData.price.toLocaleString("en-IN")}</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Lowest</p>
              <p className="text-base font-bold text-green-600">₹{lowestPrice.toLocaleString("en-IN")}</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center border border-gray-100 shadow-sm">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-1">Highest</p>
              <p className="text-base font-bold text-red-600">₹{highestPrice.toLocaleString("en-IN")}</p>
            </div>
          </div>
        </div>

        {/* Vertical Divider Line */}
        <div className="hidden lg:block w-[1px] h-32 bg-slate-100 self-center"></div>

        {/* Verdict Column - Streamlined Height */}
        <div className="flex flex-col lg:pl-12">
          <div className="p-6 md:p-8 rounded-[28px] border transition-all duration-700 shadow-[0_4px_20px_rgba(126,84,242,0.05)] flex flex-col items-center text-center relative overflow-hidden" style={{ backgroundColor: verdict.bg, borderColor: verdict.color + '20' }}>
            <div className="absolute top-0 left-0 w-full h-1" style={{ backgroundColor: verdict.color }}></div>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="text-4xl animate-float">{verdict.emoji}</span>
              <div className="flex flex-col items-start">
                <span className="px-3 py-1 rounded-full text-[11px] font-black bg-white/90 border border-black/5 uppercase tracking-[0.2em] mb-1" style={{ color: verdict.color }}>
                  Score: {totalScore}
                </span>
                <h4 className="text-3xl font-black uppercase tracking-tight" style={{ color: verdict.color }}>
                  {verdict.heading}
                </h4>
              </div>
            </div>

            <p className="text-base font-bold text-slate-800 mb-4 leading-tight">
              {verdict.subheading}
            </p>

            <div className="bg-white/70 backdrop-blur-sm p-5 rounded-2xl border border-white/50">
              <p className="text-sm text-slate-600 leading-[1.6] font-medium text-justify">
                {verdict.summary}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealScore;
